import React, { FunctionComponent, useState } from 'react';
import { canSideRotationIsAvailable } from 'src/components/pages/products/helpers';
import { getStartingUnit, Unit } from 'src/components/widgets/areaDesigner/helpers';
import Loader from 'src/components/widgets/loader';
import { useUpgradeDialogs } from 'src/shared/dialogs';
import { hoverOpacity } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import MdAdd from 'src/shared/icons/plus-solid.svg';
import MdReplaceImage from 'src/shared/icons/replace-image.svg';
import MdDelete from 'src/shared/icons/trash-alt.svg';
import styled from 'styled-components';
import { BasicButton, Link } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import { Side } from '../../state/side';
import PrintAreaPreview from '../3-printAreas/printAreaPreview';

const Cell = styled.div`
    flex: 1;
    border: 1px ${(props) => props.theme.app.primary_light} dotted;
    display: block;
    flex-direction: column;

    &:not(:last-child) {
        border-right: none;
    }
`;

const Header = styled.div<{ $disabled: boolean }>`
    padding: 15px 2px;
    font-weight: ${(props) => props.theme.text.boldWeight};
    border-bottom: 1px ${(props) => props.theme.app.primary_light} dotted;
    height: 57px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    position: relative;
    padding: 20px;

    span {
        display: inline-block;
        width: 220px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    ${Icon} {
        position: absolute;
        top: 16px;
        right: 10px;
    }

    ${(props) =>
        props.$disabled &&
        `
        span {
            color: #CCC;
        }
    `}
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    height: 340px;
    padding: 32px;
    user-select: none;
`;

const ImageContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`;

const AddIcon = styled.div`
    ${Icon} {
        width: 35px;
        height: 35px;
    }
`;

const Buttons = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    margin-top: 24px;

    ${BasicButton} {
        padding: 6px 12px;
        width: 192px;
    }
`;

const PrintAreaBorder = styled.div`
    height: 100%;
    position: relative;
`;

const CheckboxContainer = styled.div`
    label {
        font-size: 12px;
    }
`;

const CornerButton = styled.div`
	position: absolute;
	top: -10%;
	left: -20%;
	width: 24px;
	height: 24px;
	border: 1px solid rgb(207, 216, 220);
	border-radius: 5px;
	background-color: ${(props) => props.theme.app.backgroundColorBase};
	display: flex;
	justify-content: center;
	align-items: center;
	${hoverOpacity()}

    ${Link} {
        color: ${(props) => props.theme.app.primary};
    }
`;

interface VariantCellProps {
    side?: Side;
    modelColor: ModelColor;
    modelSide?: ModelSide | null;
    isToggleEnabled?: boolean;
    isAreaChangeEnabled?: boolean;
    dummy?: boolean;
    onImageChangeClick?: () => void;
    onAreaChangeClick?: () => void;
    onToggleClick?: () => void;
    onSetMandatorySide?: (value: boolean) => void;
    onSetRotateSide?: (value: boolean) => void;
    editOnlyVariants?: boolean;
}

const VariantCell: FunctionComponent<VariantCellProps> = ({
    side,
    modelColor,
    modelSide,
    dummy,
    isToggleEnabled,
    isAreaChangeEnabled,
    onImageChangeClick,
    onToggleClick,
    onAreaChangeClick,
    onSetMandatorySide,
    onSetRotateSide,
    editOnlyVariants
}) => {
    const { state } = useProductConfigurationState();
    const [unit, setUnit] = useState<Unit>(getStartingUnit());
    const { getUploadStatus } = useModelSideManager();
    const { showPlanUpgradeDialog } = useUpgradeDialogs();

    const uploadStatus = modelSide ? getUploadStatus(modelSide) : { uploading: false, progress: 0 };
    const disabled = !modelSide || !!state.disabledModelSides.find((x) => x.colorGuid == modelColor.guid && x.sideGuid == modelSide.originalSideGuid);

    const [canUseRotationOrMandatory, textDisable] = canSideRotationIsAvailable(state.product, modelSide, modelColor);

    const handleChangeImageClick = () => {
        if (modelSide && onImageChangeClick) {
            onImageChangeClick();
        }
    };

    const handleChangePrintAreaClick = () => {
        if (isAreaChangeEnabled) {
            if (modelSide && onAreaChangeClick) {
                onAreaChangeClick();
            }
        } else {
            showPlanUpgradeDialog(T._('To create different print areas for different variants you must upgrade your plan.', 'Admin'));
        }
    };

    const handleToggleClick = () => {
        if (isToggleEnabled) {
            onToggleClick?.();
        } else {
            showPlanUpgradeDialog(T._('To disable a side for a specific variant you must upgrade your plan.', 'Admin'));
        }
    };

    const getPrintAreaButtonText = (modelSide?: ModelSide | null) => {
        let buttonText = T._('Add print area', 'Admin');
        if (modelSide && modelSide.areas.length > 0) {
            buttonText =
                (Math.round((modelSide.areas[0].boundsWidth / modelSide.ppcm / unit.ratio) * 10) / 10).toString() +
                ' × ' +
                (Math.round((modelSide.areas[0].boundsHeight / modelSide.ppcm / unit.ratio) * 10) / 10).toString() +
                ' ' +
                unit.label;
        }
        return buttonText;
    };

	return (
		<Cell>
			<Header $disabled={disabled}>
				<span>{side?.name}</span>
				{!dummy && !editOnlyVariants && (
					<ZTooltip title={!disabled ? T._('Disable the side for the variant', 'Admin') : undefined}>
						<Icon $hoverable onClick={handleToggleClick}>
							<MdDelete />
						</Icon>
					</ZTooltip>
				)}
			</Header>

			<Content>
				<ImageContent>
					{!dummy && disabled && (
						<AddIcon>
							<Icon $hoverable onClick={handleToggleClick}>
								<MdAdd />
							</Icon>
						</AddIcon>
					)}
					{modelSide && !disabled && !uploadStatus.uploading && (
						<PrintAreaBorder>
							<PrintAreaPreview modelSide={modelSide} product={state.product}></PrintAreaPreview>
							<ZTooltip
								position={'bottom'}
								html={
									<>
										{T._(
											`Replace the image if you'd like to use a different visual or if it doesn't match this variant/side. Remember, this is the product image your customers will see in the customizer when they select this variant/side.`,
											'Admin'
										)}
									</>
								}
							>
								<CornerButton onClick={handleChangeImageClick}>
									<Icon $hoverable>
										<MdReplaceImage />
									</Icon>
								</CornerButton>
							</ZTooltip>
						</PrintAreaBorder>
					)}
					{modelSide && !disabled && uploadStatus.uploading && uploadStatus.uploading && <Loader />}
				</ImageContent>

                {!dummy && !editOnlyVariants && (
                    <Buttons>
                        <ZTooltip
                            position={'top'}
                            html={<>{!modelSide || modelSide.areas.length === 0 ? T._('Add print area', 'Admin') : T._('Edit print area', 'Admin')}</>}
                        >
                            <BasicButton size='fullWidth' onClick={handleChangePrintAreaClick}>
                                <span>{getPrintAreaButtonText(modelSide)}</span>
                            </BasicButton>
                        </ZTooltip>
                    </Buttons>
                )}

                {!dummy && !editOnlyVariants && modelSide && !disabled && (
                    <CheckboxContainer>
                        <Checkbox
                            label={T._('Side rotation in UI', 'Admin')}
                            checked={modelSide?.canRotate || false}
                            disabled={!canUseRotationOrMandatory}
                            onChange={(e) => {
                                onSetRotateSide?.(e);
                            }}
                            helpTooltip={
                                <>
                                    {T._(
                                        "Enable this feature to allow end customers to rotate the product within the UI canvas. Ideal for products customizable in both vertical and horizontal formats based on the end customer's preference. ",
                                        'Admin'
                                    )}
                                    <br /> <br />
                                    {!canUseRotationOrMandatory && <em>{textDisable}</em>}
                                </>
                            }
                            helpTooltipPosition='right'
                        />
                        {state.sides.length > 1 && (
                            <Checkbox
                                label={T._('Customization mandatory', 'Admin')}
                                checked={modelSide.mandatory || false}
                                onChange={(value) => onSetMandatorySide?.(value)}
                                helpTooltip={
                                    <>
                                        {T._(
                                            'Making customization mandatory will ensure that this side is customized by your customers before the product can be added to the cart and purchased.',
                                            'Admin'
                                        )}
                                    </>
                                }
                                helpTooltipPosition='right'
                            />
                        )}
                    </CheckboxContainer>
                )}
            </Content>
        </Cell>
    );
};

export default VariantCell;
